import React, { useCallback, useState } from 'react';
import {
    Box,
    Button,
    Collapse,
    Dialog,
    DialogContent,
    IconButton,
    LinearProgress,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { SocialLogin } from '../../SocialLogin/SocialLogin';
import { InstagramGlyph, TiktokGlyph, YoutubeGlyph } from '../SocialPlatformSvgs';

import {
    getBuzzBridgeProfile,
    postInfluencerOAuthConnect,
    saveManualInstagramLink,
} from '../../../redux/buzzbridge/actions';
import { getJWtDetails, useDispatchPromise, showToastMessage } from '../../../utils/AppUtils';
import { useSelector } from 'react-redux';
import { buzzBridgeProfileSelector } from '../../../redux/buzzbridge/selectors';
import { resolveInstagramConnectProvider } from '../../../utils/instagramOAuthProvider';
import { buzzBridgeConfigSelector } from '../../../selectors/selectors';
import { ManualPlatformLinkForm } from '../ManualPlatformLinkForm';
import {
    EMPTY_MANUAL_PLATFORM_LINK,
    isManualPlatformLinkComplete,
    manualPlatformLinkFromStats,
    type IManualPlatformLinkData,
} from '../../../utils/manualPlatformLinkUtils';
import { resolveInfluencerDisplayAvatarUrl } from '../../../utils/influencerSocialAvatar';
import {
    type CreatorSocialPlatformId,
    canConnectAllCreatorPlatforms,
    influencerPlatformManualLabels,
    influencerPlatformSaveLabel,
    isInfluencerPlatformAutoLinkEnabled,
} from '../../../utils/influencerSocialAutoLink';

const GOLD = '#ffc965';
const MINT = '#00fdc1';
const SURFACE = '#0e0e0e';
const SURFACE_LOW = '#131313';
const SURFACE_HI = '#262626';

type OAuthProvider = 'meta' | 'instagram' | 'tiktok' | 'youtube';

interface InfluencerOnboardingModalProps {
    open: boolean;
    onClose: () => void;
    onProfileReady?: () => void;
}

export const InfluencerOnboardingModal: React.FC<InfluencerOnboardingModalProps> = ({
    open,
    onClose,
    onProfileReady,
}) => {
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];
    const muiTheme = useTheme();
    const fullScreen = useMediaQuery(muiTheme.breakpoints.down('sm'));
    const dispatchPromise = useDispatchPromise();
    const buzzProfile = useSelector(buzzBridgeProfileSelector);
    const influencer = buzzProfile?.data?.influencer;
    const hasInfluencer = Boolean(influencer?._id);
    const { isSignedIn } = getJWtDetails();
    const canConnectAllPlatforms = canConnectAllCreatorPlatforms(isSignedIn);
    const [savedInfluencerId, setSavedInfluencerId] = useState<string | undefined>();
    const activeInfluencerId = influencer?._id ?? savedInfluencerId;

    const buzzBridgeConfig = useSelector(buzzBridgeConfigSelector);
    const anyManualMode =
        !isInfluencerPlatformAutoLinkEnabled(buzzBridgeConfig, 'instagram') ||
        !isInfluencerPlatformAutoLinkEnabled(buzzBridgeConfig, 'tiktok') ||
        !isInfluencerPlatformAutoLinkEnabled(buzzBridgeConfig, 'youtube');

    const [connecting, setConnecting] = useState<OAuthProvider | null>(null);
    const [manualEntryOpen, setManualEntryOpen] = useState<CreatorSocialPlatformId | null>(null);
    const [manualDrafts, setManualDrafts] = useState<Partial<Record<CreatorSocialPlatformId, IManualPlatformLinkData>>>(
        {},
    );
    const [savingManualPlatform, setSavingManualPlatform] = useState<CreatorSocialPlatformId | null>(null);

    const manualDataFor = useCallback(
        (platform: CreatorSocialPlatformId): IManualPlatformLinkData => {
            if (manualDrafts[platform]) return manualDrafts[platform]!;
            const stats = influencer?.[platform];
            if (stats?.username) return manualPlatformLinkFromStats(stats);
            return { ...EMPTY_MANUAL_PLATFORM_LINK };
        },
        [manualDrafts, influencer],
    );

    const runManualSave = useCallback(
        async (platform: CreatorSocialPlatformId) => {
            if (!isSignedIn && platform !== 'instagram') {
                showToastMessage('Log in to connect TikTok and YouTube.', 'info');
                return;
            }
            const data = manualDataFor(platform);
            if (
                !isManualPlatformLinkComplete(data, {
                    existingHeadshotUrl: resolveInfluencerDisplayAvatarUrl(influencer),
                })
            ) {
                showToastMessage('Please fill in all fields and upload your headshot.', 'error');
                return;
            }
            setSavingManualPlatform(platform);
            try {
                const newId = await dispatchPromise(
                    saveManualInstagramLink({
                        platform,
                        influencerId: activeInfluencerId,
                        data,
                    }),
                );
                if (typeof newId === 'string') {
                    setSavedInfluencerId(newId);
                }
                setManualEntryOpen(null);
                onProfileReady?.();
            } catch {
                // toast via saga
            } finally {
                setSavingManualPlatform(null);
            }
        },
        [isSignedIn, manualDataFor, activeInfluencerId, dispatchPromise, onProfileReady, influencer],
    );

    const runConnect = useCallback(
        async (code: string, provider: OAuthProvider) => {
            if (!isSignedIn && provider !== 'meta' && provider !== 'instagram') {
                showToastMessage('Log in to connect TikTok and YouTube.', 'info');
                return;
            }
            try {
                await dispatchPromise(postInfluencerOAuthConnect({ code, provider }));
                await dispatchPromise(getBuzzBridgeProfile({ skipRefresh: false }));
                showToastMessage('Account linked successfully.', 'success');
                onProfileReady?.();
            } catch (err: unknown) {
                const e = err as {
                    response?: { data?: { message?: string } };
                    data?: { message?: string };
                    message?: string;
                };
                const msg =
                    e?.response?.data?.message ||
                    e?.data?.message ||
                    (typeof e?.message === 'string' ? e.message : null) ||
                    'Could not link this account.';
                showToastMessage(msg, 'error');
            } finally {
                setConnecting(null);
            }
        },
        [dispatchPromise, isSignedIn, onProfileReady],
    );

    const linkButton = (label: string, busy: boolean) => (
        <Button
            fullWidth
            sx={{
                py: 1.5,
                borderRadius: 999,
                fontFamily: FONT_ENUM.MAIN_BOLD,
                textTransform: 'none',
                bgcolor: SURFACE_HI,
                color: '#fff',
                '&:hover': { bgcolor: GOLD, color: '#392700' },
            }}>
            {busy ? '…' : label}
        </Button>
    );

    const textFieldSx = {
        '& .MuiOutlinedInput-root': {
            bgcolor: SURFACE_HI,
            borderRadius: 2,
            color: '#fff',
            '& fieldset': { border: 'none' },
        },
        '& input': { color: '#fff' },
    };

    const fieldLabelSx = {
        fontSize: 10,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.15em',
        fontWeight: 700,
        mb: 1,
        ml: 0.5,
        color: t.form_label,
    };

    const platformCard = (
        platform: CreatorSocialPlatformId,
        title: string,
        subtitle: string,
        glyph: React.ReactNode,
        provider: OAuthProvider,
        opts: { recommended?: boolean; connected?: boolean; disabled?: boolean },
    ) => {
        const busy = connecting === provider;
        const locked = opts.disabled && !opts.connected;
        const autoLinkEnabled = isInfluencerPlatformAutoLinkEnabled(buzzBridgeConfig, platform);
        const manualOpen = manualEntryOpen === platform;
        const manualComplete = isManualPlatformLinkComplete(manualDataFor(platform), {
            existingHeadshotUrl: resolveInfluencerDisplayAvatarUrl(influencer),
        });
        const isSaving = savingManualPlatform === platform;

        return (
            <Box
                sx={{
                    p: 3,
                    borderRadius: 2,
                    bgcolor: SURFACE_LOW,
                    border: '1px solid transparent',
                    transition: 'all 0.2s',
                    gridColumn: manualOpen ? { sm: '1 / -1' } : undefined,
                    '&:hover': { borderColor: `${GOLD}33`, bgcolor: '#171717' },
                }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                    <Box
                        sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            bgcolor: SURFACE_HI,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                        {glyph}
                    </Box>
                    {opts.recommended && (
                        <Typography
                            sx={{
                                fontSize: 10,
                                fontWeight: 800,
                                letterSpacing: '0.12em',
                                textTransform: 'uppercase',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 999,
                                bgcolor: `${MINT}18`,
                                color: MINT,
                            }}>
                            Recommended
                        </Typography>
                    )}
                    {opts.connected && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: MINT }}>
                            <CheckCircleIcon sx={{ fontSize: 18 }} />
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    fontWeight: 800,
                                    letterSpacing: '0.1em',
                                    textTransform: 'uppercase',
                                }}>
                                {autoLinkEnabled ? 'Connected' : 'Manual'}
                            </Typography>
                        </Box>
                    )}
                    {locked && <LockOutlinedIcon sx={{ fontSize: 20, color: t.placeholderGray }} />}
                </Box>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: '1.15rem', color: '#fff', mb: 0.5 }}>
                    {title}
                </Typography>
                <Typography sx={{ fontSize: 13, color: t.form_label, mb: 2 }}>
                    {opts.connected
                        ? `@${influencer![platform]!.username}`
                        : !autoLinkEnabled
                        ? 'Enter your stats manually — OAuth is not available right now.'
                        : subtitle}
                </Typography>
                {opts.connected ? (
                    <Button fullWidth disabled sx={{ py: 1.5, borderRadius: 999, color: t.form_label }}>
                        Connected
                    </Button>
                ) : locked ? (
                    <Button fullWidth disabled sx={{ py: 1.5, borderRadius: 999, color: t.form_label }}>
                        {!isSignedIn ? 'Log in to connect' : 'Connect Instagram first'}
                    </Button>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {autoLinkEnabled && (
                            <SocialLogin
                                provider={provider}
                                onClick={() => setConnecting(provider)}
                                onCodeReceived={code => void runConnect(code, provider)}
                                onAuthComplete={ok => {
                                    if (!ok) setConnecting(null);
                                }}
                                customButton={linkButton('Link account', busy)}
                            />
                        )}
                        {!autoLinkEnabled && (
                            <>
                                <Button
                                    fullWidth
                                    onClick={() => setManualEntryOpen(prev => (prev === platform ? null : platform))}
                                    sx={{
                                        py: 1.5,
                                        borderRadius: 999,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        textTransform: 'none',
                                        bgcolor: SURFACE_HI,
                                        color: '#fff',
                                        '&:hover': { bgcolor: GOLD, color: '#392700' },
                                    }}>
                                    {manualOpen
                                        ? 'Close'
                                        : manualComplete
                                        ? 'Edit manual profile'
                                        : 'Enter stats manually'}
                                </Button>
                                <Collapse in={manualOpen}>
                                    <ManualPlatformLinkForm
                                        value={manualDataFor(platform)}
                                        onChange={data => setManualDrafts(prev => ({ ...prev, [platform]: data }))}
                                        showAutoLinkUnavailableNotice
                                        existingHeadshotUrl={resolveInfluencerDisplayAvatarUrl(influencer)}
                                        {...influencerPlatformManualLabels(platform)}
                                        mutedColor={t.form_label}
                                        highlightColor={GOLD}
                                        textFieldSx={textFieldSx}
                                        fieldLabelSx={fieldLabelSx}
                                    />
                                    <Button
                                        fullWidth
                                        onClick={() => void runManualSave(platform)}
                                        disabled={!manualComplete || isSaving}
                                        sx={{
                                            mt: 2.5,
                                            py: 1.5,
                                            borderRadius: 999,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            textTransform: 'none',
                                            bgcolor: GOLD,
                                            color: '#392700',
                                            '&:hover': { opacity: 0.9, bgcolor: GOLD },
                                            '&.Mui-disabled': { bgcolor: SURFACE_HI, color: t.form_label },
                                        }}>
                                        {isSaving ? 'Saving…' : influencerPlatformSaveLabel(platform)}
                                    </Button>
                                </Collapse>
                            </>
                        )}
                        {autoLinkEnabled && (
                            <Button
                                fullWidth
                                onClick={() => setManualEntryOpen(prev => (prev === platform ? null : platform))}
                                sx={{
                                    py: 1.5,
                                    borderRadius: 999,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    textTransform: 'none',
                                    bgcolor: 'transparent',
                                    color: GOLD,
                                    border: `1px dashed ${GOLD}55`,
                                    '&:hover': { bgcolor: `${GOLD}14` },
                                }}>
                                {manualOpen ? 'Close' : 'Add manually'}
                            </Button>
                        )}
                        {autoLinkEnabled && (
                            <Collapse in={manualOpen}>
                                <ManualPlatformLinkForm
                                    value={manualDataFor(platform)}
                                    onChange={data => setManualDrafts(prev => ({ ...prev, [platform]: data }))}
                                    existingHeadshotUrl={resolveInfluencerDisplayAvatarUrl(influencer)}
                                    {...influencerPlatformManualLabels(platform)}
                                    mutedColor={t.form_label}
                                    highlightColor={GOLD}
                                    textFieldSx={textFieldSx}
                                    fieldLabelSx={fieldLabelSx}
                                />
                                <Button
                                    fullWidth
                                    onClick={() => void runManualSave(platform)}
                                    disabled={!manualComplete || isSaving}
                                    sx={{
                                        mt: 2.5,
                                        py: 1.5,
                                        borderRadius: 999,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        textTransform: 'none',
                                        bgcolor: GOLD,
                                        color: '#392700',
                                        '&:hover': { opacity: 0.9, bgcolor: GOLD },
                                        '&.Mui-disabled': { bgcolor: SURFACE_HI, color: t.form_label },
                                    }}>
                                    {isSaving ? 'Saving…' : influencerPlatformSaveLabel(platform)}
                                </Button>
                            </Collapse>
                        )}
                    </Box>
                )}
            </Box>
        );
    };

    const igConnected = Boolean(influencer?.instagram?.username);
    const ttConnected = Boolean(influencer?.tiktok?.username);
    const ytConnected = Boolean(influencer?.youtube?.username);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen={fullScreen}
            maxWidth="sm"
            fullWidth
            scroll="paper"
            PaperProps={{
                sx: {
                    bgcolor: SURFACE,
                    backgroundImage: 'none',
                    maxHeight: fullScreen ? '100%' : 'min(92vh, 880px)',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}>
            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 2,
                    borderBottom: `1px solid ${SURFACE_HI}`,
                    bgcolor: `${SURFACE}99`,
                    backdropFilter: 'blur(12px)',
                }}>
                <Typography
                    sx={{
                        fontWeight: 900,
                        letterSpacing: '-0.02em',
                        color: GOLD,
                        fontSize: 18,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                    }}>
                    BuzzBridge
                </Typography>
                <IconButton onClick={onClose} sx={{ color: t.form_label }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent sx={{ px: 3, pb: 2, pt: 3, flex: '1 1 auto', overflowY: 'auto' }}>
                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 1 }}>
                        <Box>
                            <Typography
                                sx={{
                                    color: GOLD,
                                    fontWeight: 800,
                                    fontSize: 12,
                                    letterSpacing: '0.15em',
                                    textTransform: 'uppercase',
                                }}>
                                Step 1 of 2
                            </Typography>
                            <Typography
                                sx={{
                                    fontWeight: 800,
                                    fontSize: 28,
                                    color: '#fff',
                                    mt: 0.5,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                }}>
                                Social identity
                            </Typography>
                        </Box>
                        <Typography sx={{ color: t.form_label, fontSize: 13 }}>
                            {anyManualMode ? 'OAuth or manual' : 'OAuth only'}
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={hasInfluencer ? 100 : 40}
                        sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: SURFACE_HI,
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 3,
                                background: `linear-gradient(90deg, ${GOLD}, #feb700)`,
                            },
                        }}
                    />
                </Box>

                <Typography sx={{ color: t.form_label, fontSize: 16, textAlign: 'center', mb: 4, lineHeight: 1.6 }}>
                    Connect your creator accounts so brands can verify your reach. Choose a platform below — use OAuth
                    when available, or enter stats manually.
                </Typography>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        gap: 2,
                    }}>
                    {platformCard(
                        'instagram',
                        'Instagram',
                        canConnectAllPlatforms
                            ? 'Connect with Instagram to verify your account.'
                            : 'Required first to create your BuzzBridge profile.',
                        <InstagramGlyph sx={{ width: 26, height: 26 }} color="#fff" />,
                        resolveInstagramConnectProvider(buzzBridgeConfig),
                        { recommended: !canConnectAllPlatforms, connected: igConnected },
                    )}
                    {platformCard(
                        'tiktok',
                        'TikTok',
                        canConnectAllPlatforms
                            ? 'Connect with OAuth to verify your account.'
                            : 'Log in to connect TikTok.',
                        <TiktokGlyph sx={{ width: 26, height: 26 }} color="#fff" />,
                        'tiktok',
                        { connected: ttConnected, disabled: !canConnectAllPlatforms },
                    )}
                    {platformCard(
                        'youtube',
                        'YouTube',
                        canConnectAllPlatforms
                            ? 'Connect with OAuth to verify your account.'
                            : 'Log in to connect YouTube.',
                        <YoutubeGlyph sx={{ width: 26, height: 26 }} color="#fff" />,
                        'youtube',
                        { connected: ytConnected, disabled: !canConnectAllPlatforms },
                    )}
                </Box>

                <Box
                    sx={{
                        mt: 4,
                        p: 2,
                        borderRadius: 2,
                        bgcolor: SURFACE_LOW,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                    }}>
                    <Typography sx={{ flex: 1, fontSize: 13, color: t.form_label }}>
                        Need help? You can finish linking later from your dashboard.
                    </Typography>
                    <Button
                        onClick={onClose}
                        sx={{ color: GOLD, fontWeight: 700, textTransform: 'none', whiteSpace: 'nowrap' }}>
                        Skip for now
                    </Button>
                </Box>
            </DialogContent>

            <Box
                sx={{
                    flexShrink: 0,
                    p: 2,
                    bgcolor: `${SURFACE_LOW}ee`,
                    backdropFilter: 'blur(16px)',
                    borderTop: `1px solid ${SURFACE_HI}`,
                }}>
                <Button
                    fullWidth
                    onClick={() => {
                        if (hasInfluencer || isSignedIn) {
                            onClose();
                            onProfileReady?.();
                        } else {
                            showToastMessage('Link a platform to create your creator profile.', 'info');
                        }
                    }}
                    sx={{
                        py: 2,
                        borderRadius: 999,
                        fontWeight: 800,
                        fontSize: 17,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        textTransform: 'none',
                        color: '#392700',
                        background: `linear-gradient(90deg, ${GOLD}, #feb700)`,
                        boxShadow: `0 10px 30px ${GOLD}33`,
                        '&:hover': { opacity: 0.95 },
                    }}>
                    {hasInfluencer || isSignedIn ? 'Continue to dashboard' : 'Continue'}
                </Button>
                <Typography
                    sx={{
                        textAlign: 'center',
                        fontSize: 10,
                        color: t.form_label,
                        mt: 1.5,
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                    }}>
                    Secure verification · OAuth 2.0
                </Typography>
            </Box>
        </Dialog>
    );
};
